import { OTHERS_COLOR, SELECTED_BRAND_COLOR } from '../../../utils/utils';
import { getFirstSeriesPalette } from '../../../utils/chartTheme';
import React, { useRef } from 'react';
import { PieChartData } from '../../../models/common/PieChartData';
import PieChart from '../pie-chart/PieChart';
import { t } from 'i18next';
import './PieChartWithLegend.scss';

interface PieChartWithLegendProps {
  chartData: PieChartData[];
  selectedBrand?: string;
  size?: 'SM' | 'MD' | 'LG' | 'XXL';
  colorPalette?: string[];
  showHalf?: boolean;
  showValues?: boolean;
  showLegend?: boolean;
  /** Opt-in: capitalize brand names in the legend and the tooltip (display only). */
  capitalizeBrands?: boolean;
}

const PieChartWithLegend: React.FC<PieChartWithLegendProps> = ({
  chartData,
  selectedBrand,
  size = 'SM',
  colorPalette = getFirstSeriesPalette(),
  showHalf = false,
  showValues = false,
  showLegend = true,
  capitalizeBrands = false,
}) => {
  const chartRef = useRef<any>(null);
  const legendRef = useRef<HTMLDivElement>(null);

  // Wykres bez elementu DOM (w trakcie ponownego montowania) nie ma instancji — `getEchartsInstance`
  // rzuciłby wtedy błędem zamiast zwrócić nic.
  const getChart = () => (chartRef.current?.ele ? chartRef.current.getEchartsInstance() : undefined);

  const handleMouseEnter = (name: string) => {
    const chart = getChart();
    if (!chart) return;
    chart?.dispatchAction({ type: 'highlight', seriesIndex: 0, name });
    chart?.dispatchAction({ type: 'showTip', seriesIndex: 0, name });
  };

  const handleMouseLeave = (name: string) => {
    const chart = getChart();
    if (!chart) return;
    chart?.dispatchAction({ type: 'downplay', seriesIndex: 0, name });
    chart?.dispatchAction({ type: 'hideTip' });
  };

  return (
    <div className="pie-chart-with-legend">
      <PieChart
        chartData={chartData}
        selectedBrand={selectedBrand}
        size={size}
        chartRef={chartRef}
        legendRef={legendRef}
        colorPalette={colorPalette}
        showHalf={showHalf}
        capitalizeBrands={capitalizeBrands}
      />
      {showLegend && (
        <div className={`stats-box__legend${capitalizeBrands ? ' capitalize' : ''}`} ref={legendRef}>
          {chartData.map((data, index) => {
            const color =
              data.name === selectedBrand
                ? SELECTED_BRAND_COLOR
                : data.name === t('others')
                ? OTHERS_COLOR
                : colorPalette[index];

            return (
              <div
                className="legend-tag"
                key={data.name}
                data-name={data.name}
                onMouseEnter={() => handleMouseEnter(data.name)}
                onMouseLeave={() => handleMouseLeave(data.name)}
              >
                <span style={{ backgroundColor: color }} />
                <span className="legend-tag__name">{data.name}</span>
                <span className="legend-tag__value">{data.value}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PieChartWithLegend;
